import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const ClinicalSummaryPanel = () => {
  const [showNewSummary, setShowNewSummary] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const [summaries, setSummaries] = useState([
    {
      id: 1,
      title: 'Cardiology Consultation Summary',
      template: 'Consultation Note',
      patient: 'Sarah Johnson',
      author: 'Dr. Sarah Williams',
      department: 'Cardiology',
      createdAt: '2026-01-14 10:30',
      sharedWith: ['Dr. Michael Chen', 'Nurse Emily Roberts', 'Dr. Lisa Thompson'],
      attachments: 2,
      status: 'finalized',
      content: 'Patient presents with stable angina. EKG shows normal sinus rhythm. Recommend continuation of current medication regimen with follow-up in 2 weeks.'
    },
    {
      id: 2,
      title: 'Daily Progress Note - ICU',
      template: 'Progress Note',
      patient: 'Sarah Johnson',
      author: 'Dr. Michael Chen',
      department: 'ICU',
      createdAt: '2026-01-14 08:00',
      sharedWith: ['Nurse Emily Roberts', 'Dr. Sarah Williams'],
      attachments: 0,
      status: 'draft',
      content: 'Patient showing improvement. Vital signs stable. Plan to transfer to step-down unit tomorrow if condition remains stable.'
    },
    {
      id: 3,
      title: 'Discharge Planning Summary',
      template: 'Discharge Summary',
      patient: 'Sarah Johnson',
      author: 'Nurse Emily Roberts',
      department: 'ICU',
      createdAt: '2026-01-13 16:45',
      sharedWith: ['Dr. Michael Chen', 'Dr. Sarah Williams', 'Case Management'],
      attachments: 3,
      status: 'finalized',
      content: 'Comprehensive discharge plan prepared. Patient education completed. Home health services arranged. Follow-up appointments scheduled.'
    }
  ]);

  const templates = [
    { value: 'consultation', label: 'Consultation Note' },
    { value: 'progress', label: 'Progress Note' },
    { value: 'discharge', label: 'Discharge Summary' },
    { value: 'procedure', label: 'Procedure Note' },
    { value: 'handoff', label: 'Handoff Summary' },
    { value: 'transfer', label: 'Transfer Summary' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'finalized':
        return 'bg-success/10 text-success border-success';
      case 'draft':
        return 'bg-warning/10 text-warning border-warning';
      case 'pending-review':
        return 'bg-primary/10 text-primary border-primary';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1">
      <div className="p-4 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            Clinical Summaries
          </h2>
          <div className="flex items-center gap-2">
            <Select
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              options={[{ value: '', label: 'All Templates' }, ...templates]}
              className="w-48"
            />
            <Button variant="default" size="sm" iconName="Plus" onClick={() => setShowNewSummary(true)}>
              New Summary
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {summaries?.map((summary) => (
          <div
            key={summary?.id}
            className="bg-background rounded-lg border border-border p-4 hover:shadow-elevation-2 transition-shadow"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{summary?.title}</h3>
                  <span className={cn(
                    "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                    getStatusColor(summary?.status)
                  )}>
                    {summary?.status?.replace('-', ' ')?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                  <span className="flex items-center gap-1">
                    <Icon name="FileText" size={14} />
                    {summary?.template}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    {summary?.patient}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Building2" size={14} />
                    {summary?.department}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 rounded-md p-3 mb-3">
              <p className="text-sm text-foreground line-clamp-2">{summary?.content}</p>
            </div>

            <div className="flex items-center gap-4 text-sm mb-3">
              <div className="flex items-center gap-2">
                <Icon name="UserCircle" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">Author:</span>
                <span className="font-medium text-foreground">{summary?.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">{summary?.createdAt}</span>
              </div>
              {summary?.attachments > 0 && (
                <div className="flex items-center gap-2">
                  <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{summary?.attachments} attachments</span>
                </div>
              )}
            </div>

            <div className="mb-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">SHARED WITH</p>
              <div className="flex flex-wrap gap-2">
                {summary?.sharedWith?.map((person, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs"
                  >
                    <Icon name="User" size={12} />
                    {person}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="xs" iconName="Eye">
                  View
                </Button>
                <Button variant="ghost" size="xs" iconName="Share2">
                  Share
                </Button>
                {summary?.status === 'draft' && (
                  <Button variant="ghost" size="xs" iconName="Edit">
                    Edit
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="xs" iconName="Download">
                Export
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClinicalSummaryPanel;